import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useSearchParams } from 'react-router-dom';
import { scroller, ScrollLink } from 'react-scroll';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { getDataTestId } from '@/js/libs/utils/get-data-test-id';
import { Media } from '../../../theme/Media';
import { Loader } from '../../Elements/Loader';
import { SCROLL_ANIMATION_DURATION_MS, SCROLL_SPY_THROTTLE } from './constants';
import { Wrapper, Item, Title } from './styled';
const ScrollItem = ScrollLink(Item);
export const HorizontalNavigationGroup = memo(function HorizontalNavigationGroup({ isLoading, items, onOffsetChange, persistKey, }) {
    const isMobile = useMediaQuery({ query: Media.xsMax });
    const refWrapper = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const notHiddenItems = useMemo(() => items.filter((item) => !item.hidden), [items]);
    const [activeId, setActiveId] = useState(notHiddenItems[0]?.id ?? '');
    const [offset, setOffset] = useState(0);
    const handleSetOffset = useCallbackRef(() => {
        const wrapper = refWrapper.current;
        if (wrapper) {
            const rect = wrapper.getBoundingClientRect();
            const distanceToTop = rect.top;
            const newOffset = -(wrapper.offsetHeight + distanceToTop + (isMobile ? 8 : 24));
            setOffset(newOffset);
            onOffsetChange?.(newOffset);
        }
    });
    const createSetActiveHandler = useCallbackRef((id) => () => {
        if (persistKey) {
            setSearchParams((prev) => {
                prev.set(persistKey, id);
                return prev;
            });
        }
        setActiveId(id);
    });
    useEffect(() => {
        handleSetOffset();
        window.addEventListener('scroll', handleSetOffset);
        window.addEventListener('resize', handleSetOffset);
        return () => {
            window.removeEventListener('scroll', handleSetOffset);
            window.removeEventListener('resize', handleSetOffset);
        };
    }, [handleSetOffset]);
    useEffect(() => {
        if (!persistKey)
            return;
        const activeTabId = searchParams.get(persistKey);
        if (activeTabId) {
            scroller.scrollTo(activeTabId, { offset });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (_jsxs(Wrapper, { ref: refWrapper, children: [isLoading && _jsx(Loader, { height: 52 }), !isLoading &&
                notHiddenItems.map((item) => !item.hidden && (_jsx(ScrollItem, { to: item.id, active: item.id === activeId, onSetActive: createSetActiveHandler(item.id), "data-testid": getDataTestId(item.dataTestId, item.id === activeId ? 'active' : 'inactive'), isDynamic: true, smooth: 'easeInOutQuint', duration: SCROLL_ANIMATION_DURATION_MS, offset: offset, spy: true, spyThrottle: SCROLL_SPY_THROTTLE, children: _jsx(Title, { children: item.title }) }, item.id)))] }));
});
